import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, Loader2, Plus, Save, Send, Trash2, X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  CHANGE_IMPACT, discardDraft, fromDateInput, policyKeys, saveDraft, SCOPE_LABELS, toDateInput,
  type BumpType, type DraftInput, type Policy, type PolicyDetail, type PolicyDraft,
  type PolicyOptions, type PublishRequest, type PublishResult,
} from '@/lib/policies';
import {
  BUMP_TYPES, isValidPolicyId, KNOWN_POLICY_TYPES, nextVersion, parseSemver, POLICY_SCOPES, suggestPolicyId,
} from '@shared/policy-core';
import { renderPolicyHtml } from '@shared/policy-render';
import PolicyPublishDialog from './policy-publish-dialog';

type Mode = 'new' | 'unpublished' | 'published';

function makeSchema(mode: Mode) {
  return z.object({
    policyId: z.string(),
    title: z.string().trim().min(1, 'Title is required'),
    description: z.string(),
    policyType: z.string(),
    policyScope: z.enum(['global', 'feature_group', 'feature_key']),
    featureGroupId: z.string(),
    featureKeyId: z.string(),
    isRequired: z.boolean(),
    bump: z.enum(['major', 'minor', 'patch']),
    contentMarkdown: z.string(),
    summaryOfChanges: z.array(z.object({ text: z.string() })),
    changelog: z.string(),
    effectiveDate: z.string(),
  }).superRefine((values, ctx) => {
    if (mode === 'published') return;
    if (!isValidPolicyId(values.policyId)) {
      ctx.addIssue({ code: 'custom', path: ['policyId'], message: 'Use letters, digits, "_" or "-" only' });
    }
    if (!values.policyType.trim()) ctx.addIssue({ code: 'custom', path: ['policyType'], message: 'Policy type is required' });
    if (values.policyScope === 'feature_group' && !values.featureGroupId) {
      ctx.addIssue({ code: 'custom', path: ['featureGroupId'], message: 'Choose a feature group' });
    }
    if (values.policyScope === 'feature_key' && !values.featureKeyId) {
      ctx.addIssue({ code: 'custom', path: ['featureKeyId'], message: 'Choose a feature' });
    }
  });
}

type DraftFormValues = z.infer<ReturnType<typeof makeSchema>>;

interface PolicyDraftEditorProps {
  mode: Mode;
  policyId?: string;
  policy: Policy | null;
  draft: PolicyDraft | null;
  options: PolicyOptions;
  /** Content to start a new version from when there is no draft yet. */
  seed?: { title: string; contentMarkdown: string };
  onCreated?: (policyId: string) => void;
  onPublished?: (result: PublishResult) => void;
  onDiscarded?: () => void;
}

function initialValues(mode: Mode, draft: PolicyDraft | null, policy: Policy | null, seed?: PolicyDraftEditorProps['seed']): DraftFormValues {
  return {
    policyId: draft?.id ?? '',
    title: draft?.title ?? seed?.title ?? policy?.title ?? '',
    description: draft?.description ?? '',
    policyType: draft?.policyType ?? '',
    policyScope: draft?.policyScope ?? 'global',
    featureGroupId: draft?.featureGroupId ?? '',
    featureKeyId: draft?.featureKeyId ?? '',
    isRequired: draft?.isRequired ?? true,
    bump: draft?.bump ?? 'minor',
    contentMarkdown: draft?.contentMarkdown ?? seed?.contentMarkdown ?? '',
    summaryOfChanges: (draft?.summaryOfChanges?.length ? draft.summaryOfChanges : ['']).map(text => ({ text })),
    changelog: draft?.changelog ?? '',
    effectiveDate: toDateInput(draft?.effectiveDate) || new Date().toISOString().slice(0, 10),
  };
}

function toDraftInput(mode: Mode, values: DraftFormValues): DraftInput {
  const input: DraftInput = {
    title: values.title.trim(),
    contentMarkdown: values.contentMarkdown,
    summaryOfChanges: values.summaryOfChanges.map(s => s.text.trim()).filter(Boolean),
    changelog: values.changelog.trim() || undefined,
    effectiveDate: fromDateInput(values.effectiveDate),
  };
  if (mode === 'published') return { ...input, bump: values.bump as BumpType };
  return {
    ...input,
    description: values.description.trim(),
    policyType: values.policyType.trim(),
    policyScope: values.policyScope,
    featureGroupId: values.policyScope === 'feature_group' ? values.featureGroupId : undefined,
    featureKeyId: values.policyScope === 'feature_key' ? values.featureKeyId : undefined,
    isRequired: values.isRequired,
  };
}

export default function PolicyDraftEditor({
  mode, policyId, policy, draft, options, seed, onCreated, onPublished, onDiscarded,
}: PolicyDraftEditorProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const schema = useMemo(() => makeSchema(mode), [mode]);
  const form = useForm<DraftFormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues(mode, draft, policy, seed),
  });
  const summaries = useFieldArray({ control: form.control, name: 'summaryOfChanges' });
  const [idTouched, setIdTouched] = useState(Boolean(draft));
  const [publishRequest, setPublishRequest] = useState<PublishRequest | null>(null);

  const [title, scope, groupId, keyId, bump, contentMarkdown, effectiveDate, isRequired] = form.watch([
    'title', 'policyScope', 'featureGroupId', 'featureKeyId', 'bump', 'contentMarkdown', 'effectiveDate', 'isRequired',
  ]);

  useEffect(() => {
    if (mode !== 'new' || idTouched) return;
    form.setValue('policyId', suggestPolicyId(scope, { title, featureGroupId: groupId, featureKeyId: keyId }));
  }, [mode, idTouched, scope, title, groupId, keyId, form]);

  const currentVersion = policy && parseSemver(policy.currentVersion) ? policy.currentVersion : null;
  const targetVersion = mode === 'published' ? nextVersion(currentVersion, bump as BumpType) : '1.0.0';
  const change = mode === 'published' ? (currentVersion ? bump : 'initial') : 'initial';
  const summaryRequired = change !== 'patch';
  const policyRequired = mode === 'published' ? Boolean(policy?.isRequired) : isRequired;

  const deferredMarkdown = useDeferredValue(contentMarkdown);
  const previewHtml = useMemo(() => renderPolicyHtml({
    title: title || 'Untitled policy',
    version: targetVersion,
    effectiveDate: fromDateInput(effectiveDate) ?? '',
    contentMarkdown: deferredMarkdown,
  }), [title, targetVersion, effectiveDate, deferredMarkdown]);

  const save = useMutation({
    mutationFn: (values: DraftFormValues) => {
      const id = mode === 'new' ? values.policyId : (policyId as string);
      return saveDraft(id, toDraftInput(mode, values), { create: mode === 'new' && !draft });
    },
    onSuccess: (detail: PolicyDetail, values) => {
      const id = mode === 'new' ? values.policyId : (policyId as string);
      queryClient.setQueryData(policyKeys.detail(id), detail);
      queryClient.invalidateQueries({ queryKey: policyKeys.all, exact: true });
    },
  });

  const discard = useMutation({
    mutationFn: () => discardDraft(policyId as string),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyKeys.all });
      toast({ title: 'Draft discarded' });
      onDiscarded?.();
    },
    onError: (error: Error) => toast({ title: 'Could not discard the draft', description: error.message, variant: 'destructive' }),
  });

  const saveFailed = (error: Error) =>
    toast({ title: 'Could not save the draft', description: error.message, variant: 'destructive' });

  const handleSave = form.handleSubmit(values => save.mutate(values, {
    onSuccess: () => {
      toast({ title: 'Draft saved', description: 'Drafts are not visible in the mobile app.' });
      if (mode === 'new') onCreated?.(values.policyId);
    },
    onError: saveFailed,
  }));

  // Save first, so the review covers exactly what is stored, then open the review.
  const handleReview = form.handleSubmit(values => save.mutate(values, {
    onSuccess: () => {
      const parsed = parseSemver(targetVersion);
      const input = toDraftInput(mode, values);
      setPublishRequest({
        version: targetVersion,
        majorVersion: parsed?.major ?? 0,
        minorVersion: parsed?.minor ?? 0,
        patchVersion: parsed?.patch ?? 0,
        title: input.title,
        contentMarkdown: input.contentMarkdown,
        summaryOfChanges: input.summaryOfChanges,
        changelog: input.changelog,
        effectiveDate: input.effectiveDate ?? '',
      });
    },
    onError: saveFailed,
  }));

  const keysForDisplay = options.keys.map(k => ({
    ...k,
    label: `${k.name}${k.featureGroup ? ` (${options.groups.find(g => g.id === k.featureGroup)?.name ?? k.featureGroup})` : ''}`,
  }));
  const busy = save.isPending || discard.isPending;

  return (
    <Form {...form}>
      <form onSubmit={handleSave} className="space-y-6">
        {draft?.summary.stale && (
          <Alert variant="destructive">
            <AlertTitle>This draft was started from version {draft.baseVersion}</AlertTitle>
            <AlertDescription>
              Version {policy?.currentVersion} has been published since. Check the content still includes its changes,
              or discard this draft and start again from the current version.
            </AlertDescription>
          </Alert>
        )}

        {mode !== 'published' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Policy</CardTitle>
              <CardDescription>
                Scope and linkage are fixed once the policy is first published.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <FormField control={form.control} name="policyScope" render={({ field }) => (
                <FormItem>
                  <FormLabel>Scope</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>
                      {POLICY_SCOPES.map(s => (
                        <SelectItem key={s} value={s}>
                          {SCOPE_LABELS[s]}{s === 'global' ? ' (whole app)' : s === 'feature_group' ? ' (integration suite)' : ' (single service)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />

              {scope === 'feature_group' && (
                <FormField control={form.control} name="featureGroupId" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Feature group</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl><SelectTrigger><SelectValue placeholder="Choose a feature group" /></SelectTrigger></FormControl>
                      <SelectContent>
                        {options.groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              )}

              {scope === 'feature_key' && (
                <FormField control={form.control} name="featureKeyId" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Feature</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl><SelectTrigger><SelectValue placeholder="Choose a feature" /></SelectTrigger></FormControl>
                      <SelectContent>
                        {keysForDisplay.map(k => <SelectItem key={k.id} value={k.id}>{k.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              )}
              {scope === 'global' && <div className="hidden md:block" />}

              <FormField control={form.control} name="policyId" render={({ field }) => (
                <FormItem>
                  <FormLabel>Policy ID</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="font-mono"
                      disabled={mode !== 'new'}
                      onChange={e => { setIdTouched(true); field.onChange(e); }}
                    />
                  </FormControl>
                  <FormDescription>
                    Permanent. Suggested to match how the mobile app looks policies up.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )} />

              <FormField control={form.control} name="policyType" render={({ field }) => (
                <FormItem>
                  <FormLabel>Policy type</FormLabel>
                  <FormControl>
                    <Input {...field} list="policy-type-options" placeholder="e.g. privacy_policy" className="font-mono" />
                  </FormControl>
                  <datalist id="policy-type-options">
                    {KNOWN_POLICY_TYPES.map(t => <option key={t} value={t} />)}
                  </datalist>
                  <FormMessage />
                </FormItem>
              )} />

              <FormField control={form.control} name="description" render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>Description</FormLabel>
                  <FormControl><Textarea rows={2} {...field} placeholder="One or two sentences shown alongside the policy" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              <FormField control={form.control} name="isRequired" render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-md border p-3 md:col-span-2">
                  <div>
                    <FormLabel>Acceptance required</FormLabel>
                    <FormDescription>
                      Required policies block the {scope === 'global' ? 'app' : 'feature'} until users accept them.
                      Optional policies are informational.
                    </FormDescription>
                  </div>
                  <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                </FormItem>
              )} />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {mode === 'published' ? `New version ${targetVersion}` : 'Version 1.0.0'}
            </CardTitle>
            <CardDescription>{CHANGE_IMPACT[change].impact}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {mode === 'published' && (
              <FormField control={form.control} name="bump" render={({ field }) => (
                <FormItem>
                  <FormLabel>Type of change</FormLabel>
                  <FormControl>
                    <RadioGroup value={field.value} onValueChange={field.onChange} className="grid gap-2 md:grid-cols-3">
                      {BUMP_TYPES.map(b => (
                        <label
                          key={b}
                          htmlFor={`bump-${b}`}
                          className="flex cursor-pointer gap-3 rounded-md border p-3 has-[[data-state=checked]]:border-slate-900"
                        >
                          <RadioGroupItem id={`bump-${b}`} value={b} className="mt-1" />
                          <span>
                            <span className="font-medium">
                              {CHANGE_IMPACT[b].label} · {nextVersion(currentVersion, b)}
                            </span>
                            <span className="block text-xs text-slate-500">{CHANGE_IMPACT[b].impact}</span>
                          </span>
                        </label>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  {!policyRequired && <FormDescription>This policy is optional, so a major change does not block users.</FormDescription>}
                </FormItem>
              )} />
            )}

            <div className="grid gap-4 md:grid-cols-[1fr_12rem]">
              <FormField control={form.control} name="title" render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl><Input {...field} placeholder="e.g. Privacy Policy" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="effectiveDate" render={({ field }) => (
                <FormItem>
                  <FormLabel>Effective date</FormLabel>
                  <FormControl><Input type="date" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <FormField control={form.control} name="contentMarkdown" render={({ field }) => (
                <FormItem>
                  <FormLabel>Content (Markdown)</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      spellCheck
                      className="min-h-[560px] font-mono text-sm leading-6"
                      placeholder={'# Privacy Policy\n\n**Effective date:** …\n\n## What we collect\n\n- …'}
                    />
                  </FormControl>
                  <FormDescription>
                    # Heading, **bold**, - list, [link](https://…). Raw HTML is shown as text.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )} />
              <div className="space-y-2">
                <p className="flex items-center gap-2 text-sm font-medium"><Eye className="h-4 w-4" /> Public page preview</p>
                <iframe
                  title="Policy preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  className="h-[560px] w-full rounded-md border bg-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <FormLabel>Summary of changes{summaryRequired ? '' : ' (optional for a patch)'}</FormLabel>
              <p className="text-sm text-slate-500">
                Plain-language points shown to users in the app{change === 'minor' ? ' and in the update email' : ''}.
              </p>
              {summaries.fields.map((item, index) => (
                <div key={item.id} className="flex gap-2">
                  <Input {...form.register(`summaryOfChanges.${index}.text`)} placeholder="e.g. We now explain how long we keep consultation notes." />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => summaries.remove(index)}
                    disabled={summaries.fields.length === 1}
                    title="Remove"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => summaries.append({ text: '' })}>
                <Plus className="h-4 w-4 mr-1" /> Add point
              </Button>
            </div>

            <FormField control={form.control} name="changelog" render={({ field }) => (
              <FormItem>
                <FormLabel>Internal changelog</FormLabel>
                <FormControl><Textarea rows={2} {...field} placeholder="Why this change was made, approvals, ticket references" /></FormControl>
                <FormDescription>Stored with the version for auditors. Not shown to users.</FormDescription>
              </FormItem>
            )} />
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {draft && mode !== 'new' && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" variant="outline" className="text-red-600 hover:text-red-700 mr-auto" disabled={busy}>
                  <Trash2 className="h-4 w-4 mr-2" /> Discard draft
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Discard this draft?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {mode === 'unpublished'
                      ? 'The policy has never been published, so it will be removed completely.'
                      : 'Published versions are not affected.'}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep</AlertDialogCancel>
                  <AlertDialogAction onClick={() => discard.mutate()}>Discard</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button type="submit" variant="outline" disabled={busy}>
            {save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save draft
          </Button>
          {mode !== 'new' && (
            <Button type="button" onClick={handleReview} disabled={busy}>
              <Send className="h-4 w-4 mr-2" /> Review and publish
            </Button>
          )}
        </div>
      </form>

      <PolicyPublishDialog
        open={publishRequest !== null}
        onOpenChange={open => { if (!open) setPublishRequest(null); }}
        policyId={policyId as string}
        policy={policy}
        isRequired={policyRequired}
        request={publishRequest}
        onPublished={result => {
          setPublishRequest(null);
          onPublished?.(result);
        }}
      />
    </Form>
  );
}
